import { useQuery } from '@tanstack/react-query';
import { ordersClient } from '@/lib/orders-client';
import { useBrandFilter } from '@/components/brand-filter/brand-filter-context';
import { selectedLocationStorageKey } from '@/components/orders/use-location-scope';

interface UsePendingOrdersCountOptions {
    enabled: boolean;
    interval?: number;
}

export function usePendingOrdersCount({ enabled, interval = 30000 }: UsePendingOrdersCountOptions) {
    const { brandIdsParam, brandFilterKey } = useBrandFilter();
    // localStorage + time window are read per fetch (not in the key) so the
    // badge follows the selected location without restarting the query.
    const { data } = useQuery({
        queryKey: ['pending-orders-count', brandFilterKey],
        queryFn: async () => {
            const stored = localStorage.getItem(selectedLocationStorageKey());
            // 'all' is the orders tab's "every branch" sentinel (Radix Select rejects '').
            const locationId = stored && stored !== 'all' ? stored : undefined;
            const now = new Date();
            const from = new Date(now.getTime() - 12 * 60 * 60 * 1000);
            const response = await ordersClient.getToday({
                locationId,
                from: from.toISOString(),
                to: now.toISOString(),
                brandIds: brandIdsParam,
            });
            // pos_push_failed counts too: a paid order that never reached the POS needs the
            // operator's attention even more urgently than a pending one.
            return response.data.filter(
                (order) => order.status === 'pending' || order.status === 'pos_push_failed',
            ).length;
        },
        enabled,
        refetchInterval: interval,
    });

    return { pendingCount: data ?? 0 };
}
